import React from 'react';
import cx from 'classnames';
import { DateRange } from 'react-date-range';
import { Grid, Para, TextButton } from 'uie/components';
import { Theme } from 'uie/components';
import { isBefore } from 'date-fns';
import { useSelector } from 'react-redux';
import { IAppState } from 'core/interfaces/IAppState';
import { getLimit } from '../../postmortems/behaviors/Billing';
import { Limit } from 'core/services/service.billing';

const { theme } = Theme;

const getAllowedDateRange = (plan: Limit) => {
  if (plan === 'unlimited') {
    // it's enterprise plan
    return {
      minDate: new Date(new Date().setDate(new Date().getDate() - 364)),
      maxDate: new Date(),
    };
  }

  const months = plan ?? 1;
  return {
    // subtract months from current date
    minDate: new Date(new Date().setMonth(new Date().getMonth() - months)),
    maxDate: new Date(),
  };
};

const minimumBetweenTwoDates = (a: Date, b: Date) => {
  return isBefore(a, b) ? b : a;
};

interface DateRangePickerProps {
  isCustomDate: boolean;
  customDateFilterState: boolean;
  serviceFilterState: boolean;
  customRangeSelection: {
    startDate: Date;
    endDate: Date;
  };
  orgCreatedTime: Date;
  onCustomRangeToggle: (event: React.MouseEvent) => void;
  onDateRangeChange: (selection: { startDate: Date; endDate: Date }) => void;
  onApplyRange: () => void;
  onCancelRange: () => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  isCustomDate,
  customDateFilterState,
  serviceFilterState,
  customRangeSelection,
  orgCreatedTime,
  onCustomRangeToggle,
  onDateRangeChange,
  onApplyRange,
  onCancelRange,
}) => {
  const plan = useSelector((state: IAppState) => state.organization.plan.p);

  const planQuantity = getLimit(plan, 'incident-data-retention');

  const allowedDateRange = getAllowedDateRange(planQuantity);
  const { minDate, maxDate } = allowedDateRange;
  const whichEverIsBefore = minimumBetweenTwoDates(minDate, orgCreatedTime);
  return (
    <div style={{ display: 'inline', position: 'relative' }}>
      <button
        className="filter-button-design"
        style={
          isCustomDate
            ? {
                border: '1px solid var(--primary-default',
                fontWeight: 600,
                color: 'var(--primary-default)',
              }
            : { border: '1px solid #DFE3EB', color: '#b6bcc8', fontWeight: 600 }
        }
        onClick={onCustomRangeToggle}
      >
        Custom Range &nbsp;&nbsp;
        <i
          className={cx('fa', {
            'fa-angle-up': serviceFilterState,
            'fa-angle-down': !serviceFilterState,
          })}
        />
      </button>
      {customDateFilterState && (
        <div
          className="filter-drop no-cursor animated fadeIn date-range-drop-down"
          style={{ top: 50, right: 6 }}
          onClick={event => {
            event.stopPropagation();
          }}
        >
          <DateRange
            minDate={whichEverIsBefore}
            maxDate={maxDate}
            direction="vertical"
            scroll={{ enabled: true }}
            ranges={[
              {
                startDate: new Date(customRangeSelection.startDate),
                endDate: new Date(customRangeSelection.endDate),
                key: 'selection',
              },
            ]}
            color={'#0f61dd'}
            onChange={(e: any) => {
              onDateRangeChange({
                startDate: e.selection.startDate,
                endDate: e.selection.endDate,
              });
            }}
          />
          <Grid justifyContent="flex-end">
            <TextButton buttonType="ghost" className="mr-20" onClick={onCancelRange}>
              <Para fontSize={14}>Cancel</Para>
            </TextButton>
            <TextButton onClick={onApplyRange}>
              <Para fontSize={14} color={theme.shades.white}>
                Apply Time Range
              </Para>
            </TextButton>
          </Grid>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
